/**
 * catalogValidation.js — Service Catalog normalization + derived validation flags
 *
 * Powers the redesigned Services pricing/operations grid. Pure functions only
 * (except loadServiceCatalog, which composes the existing servicesApiV2 calls).
 *
 * Validation rules (from the design handoff — drive all red/amber flags):
 *   - Missing price (null)                 → ERROR   rendered "——"      (danger, bold)
 *   - Zero price (0)                       → ERROR   rendered "$0.00"   (danger, bold)
 *   - Inverted (retail < wholesale, both present & non-zero) → WARNING  (amber cell)
 *   - Flat (all 4 retail sizes identical & valid)           → WARNING  ("FLAT" badge)
 *   - Missing quantity / missing duration  → operational WARNING (amber, ops grid)
 *   - Category rollup = sum of child errors/warnings; 0/0 → "Clean"
 *
 * IMPORTANT: prices are built from raw pricing records so that a *missing* record
 * (null) stays distinct from a record priced at 0. Do NOT feed extractPriceMatrix
 * output in here — it collapses null → 0 and would mislabel every gap as a zero error.
 */

import ServicesAPI, {
  getMasterServicesByInstaller,
  getInstallerServices,
  getServiceCategories,
  buildCategoryLookup,
  getAllTrimOverrides,
} from '../../../services/servicesApiV2';
import { isMarineService, getMarineRate } from '../../../util/marineService';

export const SIZES = ['S', 'M', 'L', 'XL'];
export const SIZE_LABELS = { XL: 'X-Large', L: 'Large', M: 'Medium', S: 'Small' };
export const EDIT_SIZE_ORDER = ['XL', 'L', 'M', 'S']; // largest first (edit page + new service)

// ----------------------------------------------------------------------------
// Formatting
// ----------------------------------------------------------------------------

/** null/undefined → null; otherwise "$1,234" (no cents). Zero is caller's concern. */
export const money = (n) => (n === null || n === undefined ? null : '$' + Number(n).toLocaleString('en-US'));

/** Grid/edit display for a price value per the validation rules. */
export const priceText = (v) => {
  if (v === null || v === undefined) return '——';
  if (v === 0) return '$0.00';
  return money(v);
};

// ----------------------------------------------------------------------------
// Normalization
// ----------------------------------------------------------------------------

const priceOf = (record) => {
  // A record exists → it has a numeric price (default 0). Supports V2 "price"
  // and legacy "service_cost".
  const p = record.price ?? record.service_cost;
  return p === null || p === undefined ? 0 : Number(p);
};

/**
 * Extract the preferred-product rows a master service carries (material items
 * with a product assigned). The server folds these into the master-services
 * response as `preferred_products`; absent → [] (icon simply won't render).
 * Work steps (type === 'work') and product-less rows are dropped.
 * @param {Object} svc - raw master service
 * @returns {Array<{ id, name, brand, label }>}
 */
export const extractPreferredProducts = (svc) => {
  const items = svc.preferred_products || svc.preferredProducts || [];
  const seen = new Set();
  const out = [];
  (items || []).forEach((t) => {
    if ((t.type ?? 'material') === 'work') return;
    const id = t.recommended_product_id ?? t.product_id ?? null;
    const name = t.recommended_product_name || t.product_name || null;
    if (id == null && !name) return;
    // De-dupe to distinct products: the same product can be attached to
    // multiple areas/steps (or arrive as repeated rows), which we show once.
    const key = id != null ? `id:${id}` : `name:${name.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ id, name, brand: t.recommended_product_brand || t.brand || null });
  });
  return out;
};

/**
 * Build the price/duration/qty matrices for one master service from its raw
 * pricing records. Missing size×tier → null (distinct from a 0 price).
 *
 * Marine (area-priced) services carry a single rate row identified by
 * `isMarineService()` (typically `vehicle_size: 'SF'`) instead of an
 * S/M/L/XL × retail/wholesale spread. That row is NOT a member of `SIZES`,
 * so the per-record gate below would silently discard it like any other
 * malformed record — it is captured separately, BEFORE that gate runs, so
 * the rate survives normalization without ever entering the automotive
 * grid (no 'SF' column, `SIZES` itself is untouched).
 * @param {Array} records - pricing records for a single master_service_id
 * @returns {{ p: Object, d: Object, q: Object, dur: (number|null), isMarine: boolean, sfRate: (number|null) }}
 *   p: { S:[retail, wholesale], M, L, XL }  (null where no record)
 *   d: { S,M,L,XL }  per-size duration (null where unset)
 *   q: { S,M,L,XL }  expected quantity (null where unset)
 *   dur: representative duration (first non-null of M,L,S,XL) or null
 *   isMarine: true if any pricing record for this service is a marine row
 *   sfRate: the marine $/sq-ft rate from that row, or null (absent/invalid)
 */
export const buildMatrices = (records) => {
  const p = {};
  const d = {};
  const q = {};
  SIZES.forEach((s) => { p[s] = [null, null]; d[s] = null; q[s] = null; });

  const marineRecord = (records || []).find((rec) => isMarineService(rec)) || null;
  const isMarine = !!marineRecord;
  const sfRate = marineRecord ? getMarineRate(marineRecord) : null;

  (records || []).forEach((rec) => {
    const size = rec.vehicle_size;
    if (!SIZES.includes(size)) return;
    const val = priceOf(rec);
    if (rec.catalog === 'RETAIL') {
      p[size][0] = val;
      const dur = rec.duration_days;
      d[size] = dur === null || dur === undefined || dur === 0 ? null : Number(dur);
      const qty = rec.expected_quantity ?? rec.expected_material_qty ?? rec.expectedQuantity;
      q[size] = qty === null || qty === undefined || qty === 0 ? null : Number(qty);
    } else if (rec.catalog === 'WHOLE') {
      p[size][1] = val;
    }
  });

  const dur = d.M ?? d.L ?? d.S ?? d.XL ?? null;
  return { p, d, q, dur, isMarine, sfRate };
};

/**
 * Normalize raw API data into the category → services tree the grid renders.
 * @param {Array} masterServices - from getMasterServicesByInstaller
 * @param {Array} pricingRecords - from getInstallerServices (all pricing)
 * @param {Array} categories     - from getServiceCategories (for names/order)
 * @param {Object} vexCountById  - { [master_service_id]: number } trim-override counts
 * @returns {Array} categories: [{ id, name, services: [normalizedService] }]
 */
export const normalizeCatalog = (masterServices, pricingRecords, categories, vexCountById = {}) => {
  // Group pricing records by master_service_id
  const byMaster = {};
  (pricingRecords || []).forEach((rec) => {
    const id = rec.master_service_id;
    if (!byMaster[id]) byMaster[id] = [];
    byMaster[id].push(rec);
  });

  const catLookup = buildCategoryLookup(categories || []);

  const services = (masterServices || []).map((svc) => {
    const { p, d, q, dur, isMarine, sfRate } = buildMatrices(byMaster[svc.id] || []);
    return {
      id: svc.id,
      name: svc.service_name,
      categoryId: svc.category_id,
      categoryName: catLookup[svc.category_id]?.name || 'Uncategorized',
      cov: svc.coverage_level || null,
      vex: vexCountById[svc.id] || 0,
      // Custom services are priced on the estimate — base price/duration MUST be $0
      // (same "name contains custom" convention as newAddService.jsx).
      isCustom: /custom/i.test(svc.service_name || ''),
      // Derived from the SF pricing record (see buildMatrices), NOT from
      // `raw` (the master-service row) — that record is where asset_type/
      // vehicle_size/service_cost actually live; the master-service row is
      // not proven to carry any of them.
      isMarine, sfRate,
      p, d, q, dur,
      products: extractPreferredProducts(svc),
      raw: svc,
    };
  });

  // Group services under their categories, ordered by category display order then name
  const catsWithServices = {};
  services.forEach((svc) => {
    if (!catsWithServices[svc.categoryId]) {
      catsWithServices[svc.categoryId] = {
        id: svc.categoryId,
        name: svc.categoryName,
        order: catLookup[svc.categoryId]?.displayOrder ?? 999,
        services: [],
      };
    }
    catsWithServices[svc.categoryId].services.push(svc);
  });

  return Object.values(catsWithServices)
    .sort((a, b) => (a.order - b.order) || a.name.localeCompare(b.name))
    .map((cat) => ({
      ...cat,
      services: cat.services.sort((a, b) => a.name.localeCompare(b.name)),
    }));
};

// ----------------------------------------------------------------------------
// Validation flags (derived at render time; never stored)
// ----------------------------------------------------------------------------

/**
 * Per-service pricing flags. Returns per-cell metadata for the 8 price cells
 * plus rollup counts and boolean states.
 */
export const serviceFlags = (svc) => {
  // `svc` here is the NORMALIZED catalog entry from normalizeCatalog(), which
  // surfaces `isMarine`/`sfRate` derived from the SF *pricing record*
  // (buildMatrices) — NOT from `svc.raw` (the master-service row). The
  // master-service row is a different object than the priced record and is
  // not proven to carry asset_type/vehicle_size/service_cost at all; the
  // pricing record confirmed does. Fall back to checking `svc` itself so a
  // raw pricing record passed directly still resolves correctly.
  const isMarine = svc.isMarine ?? isMarineService(svc);
  if (isMarine) {
    const rate = svc.sfRate !== undefined ? svc.sfRate : getMarineRate(svc);
    const cells = [];
    let err = 0;
    if (!rate || rate <= 0) {
      err++;
      cells.push({
        size: 'SF', tier: 'R', state: 'error',
        text: priceText(rate), title: 'Marine service must have a $/sq ft rate',
      });
    }
    const noDur = svc.dur === null || svc.dur === undefined || svc.dur === 0;
    return {
      cells, flat: false, err, warn: 0, noDur,
      hasIssue: err > 0, isCustom: false, isMarine: true,
    };
  }

  const isCustom = !!svc.isCustom;
  let err = 0;
  let warn = 0;
  const retails = SIZES.map((s) => svc.p[s][0]);
  const allRetailValid = retails.every((v) => v !== null && v !== undefined && v !== 0);
  // Flat pricing is usually intentional (fixed-fee / package price): informational
  // badge only, NOT counted toward warnings. Never on custom services.
  const flat = !isCustom && allRetailValid && retails.every((v) => v === retails[0]);

  const cells = [];
  SIZES.forEach((s) => {
    const [r, w] = svc.p[s];
    const inverted = !isCustom && r != null && w != null && r !== 0 && w !== 0 && r < w;
    if (inverted) warn++;
    [['R', r], ['W', w]].forEach(([tier, val]) => {
      const cell = { size: s, tier, state: 'ok', text: priceText(val), title: '' };
      if (isCustom) {
        // Custom services MUST be $0 (priced on the estimate). $0 is correct;
        // a non-zero price is the error. Null (no record) is treated as unset/ok.
        if (val != null && val !== 0) {
          cell.state = 'error'; cell.title = `${s} ${tier} — custom service must be $0`; err++;
        }
      } else if (val === null || val === undefined) {
        cell.state = 'error'; cell.title = `${s} ${tier} — no price`; err++;
      } else if (val === 0) {
        cell.state = 'error'; cell.title = `${s} ${tier} — $0.00`; err++;
      }
      if (inverted) { cell.state = 'warn'; cell.title = `${s} — retail < wholesale`; }
      cells.push(cell);
    });
  });

  // Duration is operational data (accrues from OBD over time) — surfaced in the
  // Operations track (serviceOpFlags), NOT counted as a pricing warning here.
  const noDur = svc.dur === null || svc.dur === undefined || svc.dur === 0;

  return { cells, flat, err, warn, noDur, hasIssue: err + warn > 0, isCustom, isMarine: false };
};

/**
 * Operational completeness for one service (Operations mode). Counts missing
 * expected-quantity and duration cells; a service "needs data" if any is absent.
 * Kept separate from serviceFlags so operational gaps never inflate pricing counts.
 * Custom services are $0 by design and never "need data".
 */
export const serviceOpFlags = (svc) => {
  if (svc.isCustom) return { missing: 0, needsData: false };
  let missing = 0;
  SIZES.forEach((s) => {
    if (svc.q[s] === null || svc.q[s] === undefined || svc.q[s] === 0) missing++;
    if (svc.d[s] === null || svc.d[s] === undefined || svc.d[s] === 0) missing++;
  });
  return { missing, needsData: missing > 0 };
};

/** Aggregate error/warning counts for a category's services. */
export const categoryFlags = (services) => {
  let err = 0;
  let warn = 0;
  (services || []).forEach((svc) => {
    const f = serviceFlags(svc);
    err += f.err; warn += f.warn;
  });
  return { err, warn, clean: err + warn === 0 };
};

/** Aggregate operational completeness for a category's services. */
export const categoryOpFlags = (services) => {
  let needs = 0;
  (services || []).forEach((svc) => { if (serviceOpFlags(svc).needsData) needs++; });
  return { needs, complete: needs === 0 };
};

/** Catalog-wide PRICING totals for the summary bar (Pricing mode). */
export const catalogTotals = (catalog) => {
  let errors = 0;
  let warnings = 0;
  let services = 0;
  (catalog || []).forEach((cat) => {
    cat.services.forEach((svc) => {
      const f = serviceFlags(svc);
      errors += f.err; warnings += f.warn; services++;
    });
  });
  return { errors, warnings, services, categories: (catalog || []).length };
};

/** Catalog-wide OPERATIONAL totals for the summary bar (Operations mode). */
export const operationalTotals = (catalog) => {
  let needsData = 0;
  let services = 0;
  (catalog || []).forEach((cat) => {
    cat.services.forEach((svc) => {
      services++;
      if (serviceOpFlags(svc).needsData) needsData++;
    });
  });
  return { needsData, services, categories: (catalog || []).length };
};

// ----------------------------------------------------------------------------
// Loader — composes existing servicesApiV2 endpoints (one pass)
// ----------------------------------------------------------------------------

/**
 * Load the full normalized catalog for an installer.
 * @param {number} installerId
 * @returns {Promise<{ catalog: Array, categories: Array }>}
 */
export const loadServiceCatalog = async (installerId) => {
  const [masterServices, pricingRecords, categories, trimOverrides] = await Promise.all([
    getMasterServicesByInstaller(installerId),
    getInstallerServices(installerId),
    getServiceCategories(),
    getAllTrimOverrides(installerId).catch(() => []),
  ]);

  // Count distinct vehicles with an override, per master service.
  const vexCountById = {};
  const seen = {}; // `${masterServiceId}:${trimId}` de-dupe (retail + wholesale rows)
  (trimOverrides || []).forEach((o) => {
    const msId = o.master_service_id ?? o.masterServiceId;
    const trimId = o.id_car_trim ?? o.trimId;
    if (!msId || !trimId) return;
    const key = `${msId}:${trimId}`;
    if (seen[key]) return;
    seen[key] = true;
    vexCountById[msId] = (vexCountById[msId] || 0) + 1;
  });

  const catalog = normalizeCatalog(masterServices, pricingRecords, categories, vexCountById);
  return { catalog, categories };
};

// Keep a default export so both named and default imports work.
const catalogValidation = {
  SIZES, SIZE_LABELS, EDIT_SIZE_ORDER,
  money, priceText, buildMatrices, normalizeCatalog,
  serviceFlags, categoryFlags, catalogTotals,
  serviceOpFlags, categoryOpFlags, operationalTotals,
  loadServiceCatalog,
};
export { ServicesAPI };
export default catalogValidation;
